import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, cursosService } from '../services/api';
import Pagination from '../components/Pagination';
import ConfirmPopup from '../components/ConfirmPopup';
import './Inscripcion.css';

const PAGE_SIZE = 15;
const formatFecha = (isoStr) => {
  if (!isoStr) return '-';
  return new Date(isoStr).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

const Inscripcion = () => {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [selectedCursoId, setSelectedCursoId] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingCursos, setLoadingCursos] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState('');
  const [aceptandoId, setAceptandoId] = useState(null);
  const [declinandoId, setDeclinandoId] = useState(null);
  const [confirmDeclinar, setConfirmDeclinar] = useState({ show: false, id: null });

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadCursos();
  }, [navigate]);

  const loadCursos = async () => {
    setLoadingCursos(true);
    setError('');
    try {
      const res = await cursosService.getPreInscripciones();
      setCursos(res?.cursos || []);
      if (res?.cursos?.length && !selectedCursoId) {
        const firstWithPre = res.cursos.find((c) => (c.total_pre_inscriptos || 0) > 0);
        if (firstWithPre) setSelectedCursoId(String(firstWithPre.id));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar cursos');
      setCursos([]);
    } finally {
      setLoadingCursos(false);
    }
  };

  const loadResults = useCallback(async () => {
    if (!selectedCursoId) {
      setResults([]);
      setTotal(0);
      return;
    }
    setLoadingResults(true);
    setError('');
    try {
      const res = await cursosService.getPreInscripciones({
        curso_id: selectedCursoId,
        page,
        page_size: PAGE_SIZE,
      });
      setResults(res?.results || []);
      setTotal(res?.total ?? 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar pre-inscripciones');
      setResults([]);
      setTotal(0);
    } finally {
      setLoadingResults(false);
    }
  }, [selectedCursoId, page]);

  useEffect(() => {
    if (selectedCursoId && !loadingCursos) loadResults();
  }, [selectedCursoId, page, loadingCursos, loadResults]);

  const handleCursoChange = (e) => {
    setSelectedCursoId(e.target.value || '');
    setPage(1);
  };

  const handleAceptar = async (preInscripcionId) => {
    setAceptandoId(preInscripcionId);
    setError('');
    try {
      await cursosService.aceptarPreInscripcion(preInscripcionId);
      await loadCursos();
      await loadResults();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al aceptar la pre-inscripción');
    } finally {
      setAceptandoId(null);
    }
  };

  const handleDeclinar = (preInscripcionId) => {
    setConfirmDeclinar({ show: true, id: preInscripcionId });
  };

  const confirmarDeclinar = async () => {
    const preInscripcionId = confirmDeclinar.id;
    setConfirmDeclinar({ show: false, id: null });
    if (!preInscripcionId) return;
    setDeclinandoId(preInscripcionId);
    setError('');
    try {
      await cursosService.declinarPreInscripcion(preInscripcionId);
      await loadCursos();
      await loadResults();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al declinar la pre-inscripción');
    } finally {
      setDeclinandoId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loadingCursos) {
    return (
      <div className="inscripcion-container">
        <div className="inscripcion-loading">
          <div className="loading-spinner" />
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inscripcion-container">
      <div className="inscripcion-header">
        <h1>Inscripción</h1>
        <p>Alumnos pre-inscriptos a tus cursos. Aceptá o decliná cada solicitud.</p>
      </div>
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <ConfirmPopup
        open={confirmDeclinar.show}
        title="Declinar pre-inscripción"
        message="¿Declinar esta pre-inscripción? El alumno no podrá volver a anotarse a este curso."
        confirmLabel="Declinar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmarDeclinar}
        onCancel={() => setConfirmDeclinar({ show: false, id: null })}
      />
      {!cursos || cursos.length === 0 ? (
        <p className="inscripcion-empty">No tenés cursos asignados como profesor.</p>
      ) : (
        <>
          <div className="inscripcion-filters">
            <label htmlFor="inscripcion-curso-select" className="inscripcion-label">Curso</label>
            <select
              id="inscripcion-curso-select"
              className="inscripcion-select"
              value={selectedCursoId}
              onChange={handleCursoChange}
            >
              <option value="">Seleccionar curso...</option>
              {cursos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                  {c.codigo ? ` (${c.codigo})` : ''}
                  {c.total_pre_inscriptos > 0 ? ` — ${c.total_pre_inscriptos} solicitud/es` : ''}
                </option>
              ))}
            </select>
          </div>
          {!selectedCursoId ? (
            <p className="inscripcion-empty">Seleccioná un curso para ver las pre-inscripciones.</p>
          ) : loadingResults && results.length === 0 ? (
            <div className="inscripcion-loading">
              <div className="loading-spinner" />
              <p>Cargando solicitudes...</p>
            </div>
          ) : results.length === 0 ? (
            <p className="inscripcion-empty">Sin pre-inscripciones pendientes en este curso.</p>
          ) : (
            <>
              <div className={`inscripcion-table-wrap ${loadingResults ? 'pagination-content-loading' : ''}`}>
                <table className="inscripcion-table">
                  <thead>
                    <tr>
                      <th>Alumno</th>
                      <th>Email</th>
                      <th>Fecha solicitud</th>
                      <th>Cursos pre-inscriptos</th>
                      <th>Completados</th>
                      <th>Abandonados</th>
                      <th>Lugar (País, Provincia, Partido, Barrio)</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((pi) => (
                      <tr key={pi.id}>
                        <td>{pi.nombre || '-'}</td>
                        <td>{pi.email || '-'}</td>
                        <td>{formatFecha(pi.fecha)}</td>
                        <td className="inscripcion-cell-list">
                          {Array.isArray(pi.cursos_preinscritos) && pi.cursos_preinscritos.length > 0
                            ? pi.cursos_preinscritos.join(', ')
                            : '-'}
                        </td>
                        <td>{pi.cursos_completados ?? 0}</td>
                        <td>{pi.cursos_abandonados ?? 0}</td>
                        <td className="inscripcion-cell-lugar">{pi.lugar || '-'}</td>
                        <td className="inscripcion-cell-actions">
                          <button
                            type="button"
                            className="inscripcion-btn inscripcion-btn-aceptar"
                            onClick={() => handleAceptar(pi.id)}
                            disabled={aceptandoId === pi.id || declinandoId === pi.id}
                          >
                            {aceptandoId === pi.id ? '...' : 'Aceptar'}
                          </button>
                          <button
                            type="button"
                            className="inscripcion-btn inscripcion-btn-declinar"
                            onClick={() => handleDeclinar(pi.id)}
                            disabled={aceptandoId === pi.id || declinandoId === pi.id}
                          >
                            {declinandoId === pi.id ? '...' : 'Declinar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Inscripcion;
